import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { CalendarDays, Info } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Returns "DD/MM/YYYY" for a YYYY-MM-DD string offset by `addDays`.
const formatRangeEnd = (startStr, addDays) => {
  if (!startStr) return '';
  const d = new Date(`${startStr}T00:00:00`);
  if (isNaN(d.getTime())) return '';
  d.setDate(d.getDate() + addDays);
  return d.toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const formatDate = (startStr) => {
  if (!startStr) return '';
  const d = new Date(`${startStr}T00:00:00`);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const PartialDatesPage = ({ user, onLogout }) => {
  const [dates, setDates] = useState({ parcial_1_start: '', parcial_2_start: '', parcial_3_start: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  useEffect(() => {
    const fetchDates = async () => {
      try {
        const res = await axios.get(`${API}/control-escolar/partial-dates`, { headers });
        setDates({
          parcial_1_start: res.data.parcial_1_start || '',
          parcial_2_start: res.data.parcial_2_start || '',
          parcial_3_start: res.data.parcial_3_start || '',
        });
      } catch (e) {
        toast.error('Error al cargar las fechas');
      } finally {
        setLoading(false);
      }
    };
    fetchDates();
    // eslint-disable-next-line
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/control-escolar/partial-dates`, {
        parcial_1_start: dates.parcial_1_start || null,
        parcial_2_start: dates.parcial_2_start || null,
        parcial_3_start: dates.parcial_3_start || null,
      }, { headers });
      toast.success('Fechas guardadas correctamente');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al guardar las fechas');
    } finally {
      setSaving(false);
    }
  };

  const clearOne = (key) => setDates((p) => ({ ...p, [key]: '' }));

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-4 md:p-6 lg:p-8 max-w-3xl mx-auto">
        <div className="flex items-center gap-3 mb-2">
          <CalendarDays className="w-7 h-7 text-teal-600" />
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900" data-testid="partial-dates-title">
            Fechas para Cargar Parciales
          </h1>
        </div>
        <p className="text-sm text-slate-600 mb-6">
          Define la <strong>fecha de inicio</strong> del periodo de carga de cada parcial. El periodo dura
          <strong> 3 días</strong> (inicio + 2 días). Los profesores verán un recordatorio 3 días antes del inicio
          hasta que inserten el separador del parcial correspondiente.
        </p>

        <div className="flex items-start gap-2 bg-blue-50 border border-blue-200 rounded-lg p-3 mb-6">
          <Info className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />
          <p className="text-sm text-blue-800">
            Puedes dejar alguno(s) en blanco. Solo se activarán los recordatorios de los parciales que tengan fecha.
          </p>
        </div>

        {loading ? (
          <p className="text-slate-500">Cargando...</p>
        ) : (
          <Card className="p-6 space-y-6">
            {[1, 2, 3].map((i) => {
              const key = `parcial_${i}_start`;
              const val = dates[key];
              return (
                <div key={i} className="border-b border-slate-100 last:border-0 pb-4 last:pb-0">
                  <label className="block text-sm font-semibold text-slate-800 mb-2">
                    Parcial {i} — fecha de inicio
                  </label>
                  <div className="flex flex-wrap items-center gap-3">
                    <Input
                      type="date"
                      value={val}
                      onChange={(e) => setDates((p) => ({ ...p, [key]: e.target.value }))}
                      className="w-48"
                      data-testid={`partial-${i}-date-input`}
                    />
                    {val && (
                      <>
                        <span className="text-sm text-slate-600" data-testid={`partial-${i}-range`}>
                          Periodo: <strong>{formatDate(val)}</strong> al <strong>{formatRangeEnd(val, 2)}</strong>
                        </span>
                        <button
                          type="button"
                          onClick={() => clearOne(key)}
                          className="text-xs text-red-500 hover:underline"
                          data-testid={`partial-${i}-clear`}
                        >
                          Quitar
                        </button>
                      </>
                    )}
                    {!val && <span className="text-sm text-slate-400">Sin definir (no se mostrarán recordatorios)</span>}
                  </div>
                </div>
              );
            })}

            <div className="pt-2">
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-teal-500 hover:bg-teal-600 text-white"
                data-testid="partial-dates-save-btn"
              >
                {saving ? 'Guardando...' : 'Guardar fechas'}
              </Button>
            </div>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default PartialDatesPage;
